import { Control, Controller, FieldErrors, FieldValues, Path, UseFormRegister } from "react-hook-form";
import { Box, InlineNotification, Text } from "@familyzone/component-library";
import { NumberFieldControl } from "./NumberFieldControl";
import {
  Checkbox,
  FormControl,
  FormErrorMessage,
  Textarea,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
} from "@chakra-ui/react";
import { ConfigFormLabel } from "./ConfigFormLabel";
import React from "react";
import { colors } from "../../../../theme";
import { CheckBoxControl } from "./CheckboxControl";

export enum UserMergeKey {
  Email = "EMAIL",
  EmailStripped = "EMAIL_STRIPPED_DOMAIN",
  Computed = "COMPUTED",
}

type Props<T extends FieldValues> = {
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  control: Control<T>;
  mergeKey: UserMergeKey;
};

export const MergeOptions = <T extends FieldValues>({ errors, control, register, mergeKey }: Props<T>): JSX.Element => {
  return (
    <>
      <Text>
        {
          "When multiple source directories contain the same users, the merge configuration defines how to combine users so they're treated as a single identity on the Qoria platform."
        }
      </Text>
      <InlineNotification
        status="warning"
        notificationTitle={"Merge priority should be unique"}
        notificationDescription={"If multiple syncs have the same merge priority, primary users will be non-deterministic."}
      />
      <NumberFieldControl
        fieldProps={{
          name: "user_merge_priority" as Path<T>,
          label: "Merge Priority",
          tooltip: "The user from the sync with the highest priority value will be considered the primary user.",
          control: control,
          errors: errors,
        }}
        min={1}
        max={99}
      />
      <Controller
        control={control}
        name={"user_merge_key" as Path<T>}
        rules={{ required: "A Merge key is required." }}
        render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => (
          <FormControl
            isInvalid={!!errors.user_merge_key}
            sx={{
              display: "flex",
              minHeight: "40px",
              gap: "16px",
            }}
          >
            <Box
              sx={{
                width: "120px",
                alignContent: "center",
                flexShrink: "0",
              }}
            >
              <ConfigFormLabel
                label={"Merge Key"}
                tooltip={"This determines the user property which is used to merge users from multiple sources."}
              />
            </Box>
            <Select
              data-testid={"merge-key-select"}
              name={name}
              ref={ref}
              iconSize={"0"}
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              size="lg"
              variant={"outline"}
              sx={{
                width: "100%",
                height: "40px",
                backgroundColor: "white",
                borderRadius: "6px",
                border: `1px solid ${colors.neutrals["30"]}`,
                paddingLeft: "16px",
              }}
            >
              <option value={UserMergeKey.Email}>Email</option>
              <option value={UserMergeKey.EmailStripped}>Email Without Domain</option>
              <option value={UserMergeKey.Computed}>Computed Expression</option>
            </Select>
            <FormErrorMessage>{error && error.message}</FormErrorMessage>
          </FormControl>
        )}
      />
      {mergeKey === UserMergeKey.Computed && (
        <InlineNotification
          status="warning"
          notificationTitle={"Computed Expression is an advanced feature for complex customer setups."}
          notificationDescription={"Please contact Qoria Support for assistance accessing this feature."}
        />
      )}
      {mergeKey === UserMergeKey.Computed && (
        <FormControl
          isInvalid={!!errors.user_merge_expression}
          sx={{
            display: "flex",
            gap: "16px",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              width: "200px",
              alignContent: "center",
              flexShrink: "0",
              minHeight: "40px",
            }}
          >
            <ConfigFormLabel label={"Merge Key Expression"} tooltip={"Expression to compute the merge key."} />
          </Box>
          <Box width={"100%"}>
            <Textarea
              disabled
              readOnly // this is only allowed to be set by identity generally
              resize="vertical"
              maxWidth="100%"
              minHeight="40px"
              {...register("user_merge_expression" as Path<T>, {
                required: mergeKey === UserMergeKey.Computed ? "An expression is required if Merge Key is Computed Expression." : false,
              })}
              sx={{ fontFamily: "monospace" }}
            />
            <FormErrorMessage>{errors.user_merge_expression && errors.user_merge_expression.message}</FormErrorMessage>
          </Box>
        </FormControl>
      )}
      <Box>
        <CheckBoxControl
          fieldProps={{
            name: "user_merge_attach_only" as Path<T>,
            label: "Attach to existing users only",
            tooltip:
              "When enabled, this sync will not create new users. Users must already exist so this source can be merged with the existing user.",
            control: control,
            errors: errors,
          }}
        />
      </Box>
    </>
  );
};

interface MergeOptionsControlProps {
  priorityValue: number;
  mergeKeyValue: UserMergeKey;
  expression?: string;
  attachOnly?: boolean;
  onExpressionChange: (value: string) => void;
  onPriorityChange: (value: string) => void;
  onMergeKeyChange: (value: UserMergeKey) => void;
  onAttachOnlyChange?: (value: boolean) => void;
}

export const MergeOptionsNoControl = ({
  priorityValue,
  mergeKeyValue,
  expression,
  attachOnly,
  onExpressionChange,
  onPriorityChange,
  onMergeKeyChange,
  onAttachOnlyChange,
}: MergeOptionsControlProps): JSX.Element => {
  return (
    <Box display={"flex"} flexDirection={"column"} gap={"0.5rem"}>
      <Text>
        {
          "When multiple source directories contain the same users, the merge configuration defines how to combine users so they're treated as a single identity on the Qoria platform."
        }
      </Text>
      <Box
        sx={{
          display: "flex",
          flexDir: "column",
          maxWidth: "400px",
          gap: "0.5rem",
          alignContent: "center",
          flexShrink: "0",
        }}
      >
        <ConfigFormLabel
          label={"Merge Priority"}
          tooltip={"The user from the sync with the highest priority value will be considered the primary user."}
        />
        <NumberInput
          min={1}
          max={99}
          sx={{
            width: "100%",
          }}
          value={priorityValue}
          onChange={(valueAsString) => {
            onPriorityChange(valueAsString);
          }}
        >
          <NumberInputField
            sx={{
              height: "40px",
              width: "100%",
              border: `1px solid ${colors.neutrals["30"]}`,
              borderRadius: "6px",
              paddingLeft: "16px",
            }}
          />
          <NumberInputStepper
            sx={{
              paddingRight: "4px",
            }}
          >
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDir: "column",
          maxWidth: "400px",
          gap: "0.5rem",
          alignContent: "center",
          flexShrink: "0",
        }}
      >
        <ConfigFormLabel
          label={"Merge Key"}
          tooltip={"This determines the user property which is used to merge users from multiple sources."}
        />
        <Select
          data-testid={"merge-key-select"}
          iconSize={"0"}
          onChange={(e) => onMergeKeyChange(e.target.value as UserMergeKey)}
          value={mergeKeyValue}
          size="lg"
          variant={"outline"}
          defaultValue={"displayName"}
          sx={{
            width: "100%",
            maxWidth: "400px",
            height: "40px",
            backgroundColor: "white",
            borderRadius: "6px",
            border: `1px solid ${colors.neutrals["30"]}`,
            paddingLeft: "16px",
          }}
        >
          <option value={UserMergeKey.Email}>Email</option>
          <option value={UserMergeKey.EmailStripped}>Email Without Domain</option>
          <option value={UserMergeKey.Computed}>Computed Expression</option>
        </Select>
      </Box>

      {mergeKeyValue === UserMergeKey.Computed && (
        <InlineNotification
          status="warning"
          notificationTitle={"Computed Expression is an advanced feature for complex customer setups."}
          notificationDescription={"Please contact Qoria Support for assistance accessing this feature."}
        />
      )}
      {mergeKeyValue === UserMergeKey.Computed && (
        <Box
          sx={{
            display: "flex",
            flexDir: "column",
            maxWidth: "400px",
            gap: "0.5rem",
            alignContent: "center",
            flexShrink: "0",
          }}
        >
          <ConfigFormLabel label={"Merge Key Expression"} tooltip={"Expression to compute the merge key."} />
          <Textarea
            disabled
            readOnly // this is only allowed to be set by identity generally
            resize="vertical"
            maxWidth="100%"
            minHeight="40px"
            sx={{ fontFamily: "monospace" }}
            value={expression || ""}
            onChange={(e) => {
              onExpressionChange(e.target.value);
            }}
          />
        </Box>
      )}
      {onAttachOnlyChange && (
        <Box
          sx={{
            display: "flex",
            gap: "0.5rem",
            alignItems: "center",
          }}
        >
          <Box display="flex" gap="0.5rem" alignItems="center">
            <Checkbox
              isChecked={attachOnly || false}
              onChange={(e) => onAttachOnlyChange(e.target.checked)}
              data-testid="attach-only-checkbox"
            />
            <ConfigFormLabel
              label={"Attach to existing users only"}
              tooltip={
                "When enabled, this sync will not create new users. Users must already exist so this source can be merged with the existing user."
              }
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
